import React from "react";
import { MarkdownPreview } from "../../../../MarkdownPreview";
import { Chip } from "../../../../Chip";
import { NumberedStep, BodyText, LanguageSelector } from "../../DeployShared";
import {
    LanguageOption,
    WorkflowParameter,
    WorkflowImage,
    API_KEY_PLACEHOLDER,
    generateHostedCode,
    replaceApiKeyPlaceholder
} from "../../DeployCodeGenerators";
import { trackEvent } from "../../../workflowUtils";
import { WorkflowEvents } from "../../../analytics/WorkflowEvents";

export interface HostedApiContentProps {
    selectedLanguage: LanguageOption;
    onLanguageSelect: (language: LanguageOption) => void;
    endpointUrl: string;
    workflowUrl: string;
    workspaceUrl: string;
    apiKey: string | null;
    workflowParameters: WorkflowParameter[];
    workflowImages: WorkflowImage[];
    isLocalEmbedded: boolean;
    isWorkflowEditor?: boolean;
    onOpenPreview?: () => void;
}

export function HostedApiContent({
    selectedLanguage,
    onLanguageSelect,
    endpointUrl,
    workflowUrl,
    workspaceUrl,
    apiKey,
    workflowParameters,
    workflowImages,
    isLocalEmbedded,
    isWorkflowEditor = true,
    onOpenPreview
}: HostedApiContentProps) {
    return (
        <div className="space-y-4 p-2">
            <div className="space-y-2">
                {isWorkflowEditor && (
                    <Chip color="green" size="xs">
                        Recommended
                    </Chip>
                )}
                <h3 className="text-lg font-semibold text-gray-900">Serverless Hosted API</h3>
            </div>

            {isLocalEmbedded ? (
                <div className="flex-1">
                    Roboflow offers{" "}
                    <a
                        href="https://inference.roboflow.com/managed/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Managed Cloud Compute
                    </a>{" "}
                    for Workflows. You're currently running locally;{" "}
                    <a
                        href="https://app.roboflow.com/workflows"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        connect to the cloud
                    </a>{" "}
                    for access.
                </div>
            ) : (
                <>
                    <BodyText>
                        Your {isWorkflowEditor ? "workflow" : "model"} is ready to use and available
                        via a hosted API endpoint. Easily integrate your{" "}
                        {isWorkflowEditor ? "workflow" : "model"} into your application by using the
                        code below.
                    </BodyText>

                    <NumberedStep
                        number={1}
                        title="Select the Coding Language You Want to Use"
                        collapsible={true}
                    >
                        <div className="flex-col !gap-2">
                            <LanguageSelector
                                selectedLanguage={selectedLanguage}
                                onLanguageSelect={onLanguageSelect}
                            />
                            <p className="mt-2 text-sm text-gray-800">
                                <span className="font-semibold">Never coded before?</span> Select
                                "HTTP / cURL" for the simplest example, or{" "}
                                {onOpenPreview ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={onOpenPreview}
                                            className="text-brand hover:text-brand-hover hover:underline"
                                        >
                                            try our no-code testing tool
                                        </button>{" "}
                                        first
                                    </>
                                ) : (
                                    <span>try our no-code testing tool first</span>
                                )}
                                .
                            </p>
                        </div>
                    </NumberedStep>

                    {selectedLanguage.value === "python" && (
                        <NumberedStep number={2} title="Install the Library" collapsible={true}>
                            <div className="flex-col !gap-2">
                                <BodyText>
                                    After running the installation command for your chosen language:
                                </BodyText>
                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <MarkdownPreview
                                        source={`\`\`\`bash
pip install -U inference-sdk
\`\`\``}
                                    />
                                </div>
                            </div>
                        </NumberedStep>
                    )}

                    <NumberedStep
                        number={selectedLanguage.value === "python" ? 3 : 2}
                        title="Copy and Run the Code"
                        collapsible={true}
                    >
                        <div className="w-full min-w-0 flex-1">
                            {(selectedLanguage.value === "javascript" ||
                                selectedLanguage.value === "curl") && (
                                <BodyText>
                                    You can pass input images as type <code>url</code> or{" "}
                                    <code>base64</code>.
                                </BodyText>
                            )}
                            <div
                                data-color-mode="light"
                                className="w-full overflow-x-auto rounded-lg border border-gray-300"
                            >
                                <MarkdownPreview
                                    source={generateHostedCode(
                                        endpointUrl,
                                        workflowUrl,
                                        workspaceUrl,
                                        API_KEY_PLACEHOLDER,
                                        workflowParameters,
                                        workflowImages,
                                        selectedLanguage.value
                                    )}
                                    onCopy={(text) => {
                                        trackEvent(WorkflowEvents.DEPLOY_CODE_SNIPPET_COPIED, {
                                            deployMode: "hosted",
                                            language: selectedLanguage.value,
                                            workflowUrl
                                        });
                                        return replaceApiKeyPlaceholder(text, apiKey);
                                    }}
                                />
                            </div>
                        </div>
                    </NumberedStep>
                </>
            )}
        </div>
    );
}
